import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useApiConfig } from './config/api';

function App() {
  const [wineName, setWineName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [wineData, setWineData] = useState(null);
  const [searchTime, setSearchTime] = useState(0);
  const [searchTimer, setSearchTimer] = useState(null);

  // Usar o hook para obter a configuração da API
  const apiConfig = useApiConfig();

  // Função para formatar o tempo em minutos e segundos
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Efeito para gerenciar o timer durante a busca
  useEffect(() => {
    if (loading) {
      // Iniciar o timer
      setSearchTime(0);
      const timer = setInterval(() => {
        setSearchTime(prevTime => prevTime + 1);
      }, 1000);
      setSearchTimer(timer);
    } else {
      // Parar o timer quando a busca terminar
      if (searchTimer) {
        clearInterval(searchTimer);
        setSearchTimer(null);
      }
    }

    // Limpar o timer quando o componente for desmontado
    return () => {
      if (searchTimer) {
        clearInterval(searchTimer);
      }
    };
  }, [loading]);





  const handleSearch = async (e) => {
    e.preventDefault();

    if (!wineName.trim()) {
      setError('Por favor, digite o nome do vinho');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Usar a configuração da API carregada do .env ou config.json
      const apiUrl = `${apiConfig.baseURL}${apiConfig.endpoints.search}`;
      console.log('=== CONFIGURAÇÃO DA API ===');
      console.log('apiConfig completo:', apiConfig);
      console.log('URL da requisição:', apiUrl);
      console.log('VITE_API_URL do .env:', import.meta.env.VITE_API_URL);
      console.log('========================');

      // Fazer a requisição direta
      const response = await axios.post(apiUrl, { wineName });

      if (response.data && response.data.success && response.data.data) {
        setWineData(response.data.data);
        toast.success('Busca concluída com sucesso!');
      } else if (response.data && !response.data.success) {
        // Caso de erro retornado pela API (vinho não encontrado com precisão)
        const errorMsg = response.data.error || 'Não foram encontradas informações precisas sobre este vinho.';
        setError(errorMsg);
        toast.warning(errorMsg, {
          autoClose: 5000
        });
        return;
      } else {
        throw new Error('Resposta da API não contém os dados esperados');
      }
    } catch (err) {
      console.error('Erro na busca:', err);
      let errorMessage = 'Erro ao buscar informações do vinho. Por favor, tente novamente.';

      if (err.response) {
        // Erro de resposta do servidor (4xx, 5xx)
        if (err.response.status === 404) {
          // Vinho não encontrado com precisão
          const errorData = err.response.data;
          errorMessage = errorData.error || 'Não foram encontradas informações precisas sobre este vinho nas fontes consultadas.';
          
          // Mostrar mensagem explicativa para o usuário
          if (errorData.codigo) {
            switch (errorData.codigo) {
              case 'WINE_NOT_FOUND_PRECISELY':
                errorMessage = `⚠️ ${errorMessage}\n\n📝 Dica: Tente buscar com o nome completo do vinho, incluindo a safra (ano) se possível.`;
                break;
              case 'PARSE_ERROR':
              case 'NO_JSON_RESPONSE':
                errorMessage = 'A IA não conseguiu processar as informações encontradas. Tente novamente com um nome mais específico.';
                break;
              case 'API_ERROR':
                errorMessage = 'Erro temporário no serviço de IA. Tente novamente em alguns instantes.';
                break;
              default:
                break;
            }
          }
        } else {
          errorMessage = `Erro ${err.response.status}: ${err.response.statusText || 'Erro no servidor'}`;
        }
      } else if (err.request) {
        // Erro de conexão (sem resposta do servidor)
        if (window.location.protocol === 'https:' && apiConfig.baseURL.startsWith('http:')) {
          errorMessage = 'Erro de Mixed Content: O frontend está em HTTPS, mas o backend está em HTTP. O servidor backend precisa ser configurado para HTTPS.';
        } else {
          errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão ou se o servidor está online.';
        }
      }

      setError(errorMessage);
      
      // Usar toast.warning para erros de vinho não encontrado, toast.error para outros erros
      if (err.response && err.response.status === 404) {
        toast.warning(errorMessage, {
          autoClose: 8000
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Função para copiar o conteúdo para a área de transferência
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success(`${label} copiado!`, {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      },
      (err) => {
        toast.error(`Erro ao copiar: ${err}`, {
          position: "top-right",
          autoClose: 2000,
        });
      }
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-800">
      <ToastContainer />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <header className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-primary-blue-700 mb-3">Sistema de Busca de Vinhos</h1>
          <p className="text-neutral-600 text-base md:text-lg">Encontre informações detalhadas sobre vinhos para cadastro</p>
        </header>

        <div className="max-w-2xl mx-auto mb-10 bg-white p-4 md:p-6 rounded-lg shadow-card">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={wineName}
              onChange={(e) => setWineName(e.target.value)}
              placeholder="Digite o nome do vinho..."
              className="flex-1 px-4 py-3 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue-500 text-neutral-800 placeholder-neutral-400 shadow-sm"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-accent-orange-500 text-white px-4 md:px-6 py-3 rounded-lg hover:bg-accent-orange-600 focus:outline-none focus:ring-2 focus:ring-accent-orange-400 disabled:opacity-50 transition-colors duration-200 shadow-sm whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Buscando...
                </span>
              ) : 'Buscar'}
            </button>
          </form>

          {error && (
            <div className="mt-4 p-4 bg-yellow-50 text-yellow-800 rounded-lg border border-yellow-200 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-semibold text-yellow-800 mb-1">
                    Vinho não encontrado com precisão
                  </h3>
                  <div className="text-sm text-yellow-700 whitespace-pre-line">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-14 w-14 border-t-4 border-b-4 border-primary-blue-600"></div>
            <p className="mt-4 text-neutral-600 text-lg">Buscando informações do vinho...</p>
          </div>
        )}

        {wineData && !loading && (
          <div className="bg-white rounded-lg shadow-card p-4 md:p-6 border border-neutral-200">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-blue-700 mb-4 md:mb-6">{wineData.nome}</h2>

            <div className="mb-8 md:mb-10">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
                <h3 className="text-xl md:text-2xl font-semibold text-neutral-800">Informações do Vinho</h3>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(wineData, null, 2), "Todas as informações")}
                  className="bg-primary-blue-500 hover:bg-primary-blue-600 text-white px-3 py-2 rounded text-sm flex items-center transition-colors duration-200 shadow-sm whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copiar Tudo
                </button>
              </div>
              <div className="overflow-x-auto rounded-lg border border-neutral-200 shadow-sm">
                <table className="min-w-full divide-y divide-neutral-200">
                  <tbody className="divide-y divide-neutral-200">
                    {[
                      { label: 'Nome original do vinho', value: wineData.nome },
                      { label: 'Uvas com suas %', value: wineData.uvas },
                      { label: 'País', value: wineData.pais },
                      { label: 'Região', value: wineData.regiao },
                      { label: 'Vinícola', value: wineData.vinicola },
                      { label: 'Ano / Safra', value: wineData.safra },
                      { label: 'Teor alcoólico', value: wineData.teor_alcoolico },
                      { label: 'Temperatura ideal', value: wineData.temperatura_ideal },
                      { label: 'Tamanho da garrafa', value: wineData.tamanho_garrafa },
                      { label: 'Classificação', value: wineData.classificacao },
                      { label: 'Tempo de potencial de guarda', value: wineData.potencial_guarda },
                      { label: 'Tipo de fechamento da garrafa', value: wineData.fechamento },
                      { label: 'Cor do vinho', value: wineData.cor },
                      { label: 'Características degustativas', value: wineData.degustativo },
                      { label: 'Características olfativas', value: wineData.olfativo },
                      { label: 'Harmonização', value: wineData.harmonizacao }
                    ].map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}>
                        <td className="px-4 py-3 text-sm font-medium text-neutral-700 w-1/3">{item.label}</td>
                        <td className="px-4 py-3 text-sm text-neutral-600 w-2/3 relative">
                          <div className="flex items-center">
                            <div className="flex-1 mr-2">{item.value}</div>
                            <button
                              onClick={() => copyToClipboard(item.value, item.label)}
                              className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 bg-primary-blue-50 hover:bg-primary-blue-100 text-primary-blue-700 rounded transition-colors duration-200 shadow-sm"
                              title={`Copiar ${item.label}`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-8 md:mb-10">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
                <h3 className="text-xl md:text-2xl font-semibold text-neutral-800">Descrição Detalhada</h3>
                <button
                  onClick={() => copyToClipboard(wineData.descricao_detalhada, "Descrição detalhada")}
                  className="bg-primary-blue-500 hover:bg-primary-blue-600 text-white px-3 py-2 rounded text-sm flex items-center transition-colors duration-200 shadow-sm whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copiar
                </button>
              </div>
              <div className="bg-neutral-50 p-4 md:p-5 rounded-lg border border-neutral-200 group shadow-sm">
                <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap text-sm md:text-base">{wineData.descricao_detalhada}</p>
              </div>
            </div>

            <div className="mb-8 md:mb-10">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
                <h3 className="text-xl md:text-2xl font-semibold text-neutral-800">Descrições para Anúncios (SEO)</h3>
                <button
                  onClick={() => copyToClipboard(wineData.descricoes_anuncio.join('\n\n'), "Descrições para anúncios")}
                  className="bg-primary-blue-500 hover:bg-primary-blue-600 text-white px-3 py-2 rounded text-sm flex items-center transition-colors duration-200 shadow-sm whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copiar Todas
                </button>
              </div>
              <div className="space-y-4">
                {wineData.descricoes_anuncio && wineData.descricoes_anuncio.map((desc, index) => (
                  <div key={index} className="bg-neutral-50 p-4 rounded-lg border border-neutral-200 shadow-sm">
                    <div className="flex items-start">
                      <div className="flex-1 mr-2">
                        <p className="text-neutral-700">{desc}</p>
                        <p className="text-xs text-neutral-500 mt-2">Caracteres: {desc.length}/200</p>
                      </div>
                      <button
                        onClick={() => copyToClipboard(desc, `Descrição ${index + 1}`)}
                        className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 bg-primary-blue-50 hover:bg-primary-blue-100 text-primary-blue-700 rounded transition-colors duration-200 shadow-sm"
                        title={`Copiar descrição ${index + 1}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
                <h3 className="text-xl md:text-2xl font-semibold text-neutral-800">Palavras-chave para SEO</h3>
                <button
                  onClick={() => copyToClipboard(wineData.palavras_chave.join(', '), "Palavras-chave")}
                  className="bg-primary-blue-500 hover:bg-primary-blue-600 text-white px-3 py-2 rounded text-sm flex items-center transition-colors duration-200 shadow-sm whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copiar Todas
                </button>
              </div>
              <div className="bg-neutral-50 p-4 md:p-5 rounded-lg border border-neutral-200 shadow-sm">
                <div className="flex flex-wrap gap-2">
                  {wineData.palavras_chave && wineData.palavras_chave.map((keyword, index) => (
                    <button
                      key={index}
                      className="bg-accent-orange-100 text-accent-orange-800 px-3 py-1.5 rounded-full text-sm border border-accent-orange-200 hover:bg-accent-orange-200 transition-colors duration-200 shadow-sm mb-2 mr-2"
                      onClick={() => copyToClipboard(keyword, "Palavra-chave")}
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;


